/**
 * InlineReviewCard — Inline categorization card replacing full-screen swipe ReviewQueue
 *
 * Default: shows merchant + AI guess pill + Confirm/Change buttons.
 * Tapping Change expands category picker with sparkle on AI guess.
 * Tapping Confirm classifies as the AI-suggested category.
 */

import { useState, useCallback } from 'react';
import { T } from '@/styles/tokens';
import { CategoryIcon } from '@/components/CategoryIcon';
import type { ArtTransaction } from '@/hooks/useArtTransactions';

interface InlineReviewCardProps {
  txn: ArtTransaction;
  onCategorize: (txn: ArtTransaction, category: string) => void;
}

/** Map tpCategory codes to human-readable display names */
const TP_TO_DISPLAY: Record<string, string> = {
  ART_SUPPLY: 'Spend',
  BUSINESS_EXPENSE: 'Spend',
  ART_INCOME: 'Income',
  PERSONAL: 'Personal',
  IGNORE: 'Ignore',
};

/** Map display names back to tpCategory codes */
const DISPLAY_TO_TP: Record<string, string> = {
  'Income': 'ART_INCOME',
  'Spend': 'ART_SUPPLY',
  'Personal': 'PERSONAL',
  'Ignore': 'IGNORE',
};

const CATEGORY_LIST = ['Income', 'Spend', 'Personal', 'Ignore'];

function formatDollars(cents: number): string {
  const dollars = Math.round(Math.abs(cents) / 100);
  return `$${dollars.toLocaleString()}`;
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** 4-point sparkle star SVG */
function Sparkle({ size = 8, color = T.primary }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <path d="M8 0L9.8 6.2L16 8L9.8 9.8L8 16L6.2 9.8L0 8L6.2 6.2L8 0Z" fill={color} />
    </svg>
  );
}

export function InlineReviewCard({ txn, onCategorize }: InlineReviewCardProps) {
  const [classified, setClassified] = useState(false);
  const [chosenCategory, setChosenCategory] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  // AI's suggested category
  const aiGuessCode = txn.tpSuggestedCategory || txn.tpCategory || 'PERSONAL';
  const aiGuessDisplay = TP_TO_DISPLAY[aiGuessCode] || 'Personal';

  const handleConfirm = useCallback((displayName: string) => {
    const tpCode = DISPLAY_TO_TP[displayName] || 'PERSONAL';
    setChosenCategory(tpCode);
    setClassified(true);
    setPicking(false);
    onCategorize(txn, tpCode);
  }, [txn, onCategorize]);

  // After classification, show a brief confirmation then fade out
  if (classified) {
    return (
      <div style={{
        background: T.cream,
        borderRadius: 14,
        padding: '10px 14px',
        border: '2px dashed #2C3440',
        opacity: 0.5,
        transition: 'opacity 280ms ease',
        overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CategoryIcon category={chosenCategory || 'UNCERTAIN'} size={24} />
          <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>
            {txn.merchantName} — classified
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: T.cream,
      borderRadius: 14,
      padding: '12px 14px',
      border: '2px dashed #2C3440',
    }}>
      {/* Transaction info: merchant + AI pill + amount */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Merchant name + AI guess pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontSize: 14,
              fontWeight: 500,
              color: T.text,
              whiteSpace: 'nowrap' as const,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {txn.merchantName}
            </span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              background: `${T.primary}0C`,
              borderRadius: 99,
              padding: '1px 8px 1px 5px',
              flexShrink: 0,
            }}>
              <Sparkle size={8} />
              <span style={{
                fontSize: 11,
                color: T.primaryDark || '#1D7A75',
                fontWeight: 500,
              }}>
                {aiGuessDisplay}?
              </span>
            </div>
          </div>
          {/* Date */}
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
            {formatShortDate(txn.date)}
          </div>
        </div>
        {/* Amount */}
        <span style={{
          fontSize: 15,
          fontWeight: 600,
          flexShrink: 0,
          marginLeft: 12,
          color: txn.amount < 0 ? '#2C9C8C' : T.text,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {txn.amount < 0 ? '+' : '\u2212'}{formatDollars(txn.amount)}
        </span>
      </div>

      {/* Action buttons */}
      {!picking ? (
        /* Default state: Confirm + Change */
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
          <button
            onClick={() => handleConfirm(aiGuessDisplay)}
            style={{
              border: 'none',
              borderRadius: 99,
              padding: '6px 20px',
              background: T.primary,
              color: 'white',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: T.fontFamily,
            }}
          >
            Confirm
          </button>
          <button
            onClick={() => setPicking(true)}
            style={{
              border: `1px solid #D5D3CD`,
              borderRadius: 99,
              padding: '5px 16px',
              background: 'transparent',
              color: '#5C5A55',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: T.fontFamily,
            }}
          >
            Change
          </button>
        </div>
      ) : (
        /* Expanded state: category picker pills */
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {CATEGORY_LIST.map((cat) => {
            const isAi = cat === aiGuessDisplay;
            return (
              <button
                key={cat}
                onClick={() => handleConfirm(cat)}
                style={{
                  border: `1px solid ${isAi ? T.primary : '#D5D3CD'}`,
                  borderRadius: 99,
                  padding: '5px 14px',
                  background: isAi ? `${T.primary}0A` : 'transparent',
                  color: isAi ? (T.primaryDark || '#1D7A75') : '#5C5A55',
                  fontSize: 12,
                  fontWeight: isAi ? 600 : 500,
                  cursor: 'pointer',
                  fontFamily: T.fontFamily,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {isAi && <Sparkle size={8} />}
                {cat}
              </button>
            );
          })}
          <button
            onClick={() => setPicking(false)}
            style={{
              border: 'none',
              background: 'transparent',
              padding: '5px 8px',
              color: '#A8A69F',
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: T.fontFamily,
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
